/**
 * registrations-ui.ts - Tournament registrations management UI
 *
 * Handles displaying and managing pending registrations
 */

import {
  getTournamentRegistrations,
  acceptRegistration,
  rejectRegistration,
  type Registration
} from '../api/registration-api';
import { escapeHtml } from '../utils/security';

/**
 * Render pending registrations section
 * @param tournamentId - Tournament ID
 * @returns HTML string for pending registrations
 */
export async function renderPendingRegistrations(tournamentId: string): Promise<string> {
  try {
    const data = await getTournamentRegistrations(tournamentId);
    const registrations = data.registrations || [];
    const pending = registrations.filter((r: Registration) => r.status === 'pending');

    if (pending.length === 0) {
      return '';
    }

    return `
      <div class="pending-registrations-section" style="margin-bottom: 20px; padding: 15px; background: #fff9e6; border: 1px solid #ffd700; border-radius: 8px;">
        <h4 style="margin: 0 0 12px 0; color: #856404; font-size: 16px;">
          ⏳ Pending Registrations (${pending.length})
        </h4>
        <ul style="list-style: none; padding: 0; margin: 0;">
          ${pending.map(reg => renderPendingRegistration(tournamentId, reg)).join('')}
        </ul>
      </div>
    `;
  } catch (error) {
    console.error('Error fetching registrations:', error);
    return '';
  }
}

/**
 * Render payment status badge
 */
function renderPaymentStatusBadge(registration: Registration): string {
  const paymentStatus = (registration as any).paymentStatus;
  const selectedFeeId = (registration as any).selectedFeeId;

  // No fee required
  if (!selectedFeeId) {
    return '';
  }

  // Paid via Stripe
  if (paymentStatus === 'captured' || (registration as any).feePaid) {
    return '<span style="display: inline-block; padding: 2px 6px; background: #d4edda; color: #155724; border-radius: 4px; font-size: 11px; margin-left: 8px;">Paid online</span>';
  }

  // Cash payment pending
  if (paymentStatus === 'cash_pending') {
    return '<span style="display: inline-block; padding: 2px 6px; background: #fff3cd; color: #856404; border-radius: 4px; font-size: 11px; margin-left: 8px;">Cash at tournament</span>';
  }

  // Stripe payment pending (waiting for payment)
  if (paymentStatus === 'pending' || paymentStatus === 'authorized') {
    return '<span style="display: inline-block; padding: 2px 6px; background: #cce5ff; color: #004085; border-radius: 4px; font-size: 11px; margin-left: 8px;">Payment pending</span>';
  }

  return '';
}

/**
 * Render a single pending registration
 */
function renderPendingRegistration(tournamentId: string, registration: Registration): string {
  const profile = registration.profile;
  if (!profile) {
    return '';
  }

  const playerName = `${profile.title ? escapeHtml(profile.title) + ' ' : ''}${escapeHtml(profile.firstName)} ${escapeHtml(profile.lastName || '')}`.trim();
  const metaItems: string[] = [];

  if (profile.rating) {
    metaItems.push(`Elo ${profile.rating}`);
  }
  if (profile.federationCode) {
    metaItems.push(escapeHtml(profile.federationCode));
  }
  if (profile.club) {
    metaItems.push(escapeHtml(profile.club));
  }

  const paymentBadge = renderPaymentStatusBadge(registration);

  return `
    <li id="pending-registration-${registration.id}" style="display: flex; align-items: center; justify-content: space-between; padding: 10px; margin-bottom: 8px; background: white; border: 1px solid #e0e0e0; border-radius: 6px;">
      <div style="flex: 1;">
        <div style="font-weight: 500; color: #333; margin-bottom: 4px;">
          ${playerName}${paymentBadge}
        </div>
        ${metaItems.length > 0 ? `
          <div style="font-size: 13px; color: #666;">
            ${metaItems.join(' • ')}
          </div>
        ` : ''}
        <div style="font-size: 12px; color: #999; margin-top: 4px;">
          Registered ${new Date(registration.createdAt).toLocaleDateString()}
        </div>
      </div>
      <div style="display: flex; gap: 8px;">
        <button
          class="btn btn-primary btn-small"
          onclick="handleAcceptRegistration('${tournamentId}', '${registration.id}')"
          style="padding: 6px 12px; font-size: 13px;">
          ✓ Accept
        </button>
        <button
          class="btn btn-secondary btn-small"
          onclick="handleRejectRegistration('${tournamentId}', '${registration.id}')"
          style="padding: 6px 12px; font-size: 13px;">
          ✗ Reject
        </button>
      </div>
    </li>
  `;
}

/**
 * Handle accepting a registration
 */
export async function handleAcceptRegistration(tournamentId: string, registrationId: string): Promise<void> {
  const regElement = document.getElementById(`pending-registration-${registrationId}`);
  if (regElement) {
    regElement.style.opacity = '0.5';
    regElement.style.pointerEvents = 'none';
  }

  try {
    await acceptRegistration(tournamentId, registrationId);

    // Save active tab before reload to stay on players tab
    sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

    // Reload tournament data
    if (typeof window.loadTournament === 'function') {
      await window.loadTournament(tournamentId);
    } else {
      // Fallback: reload page
      window.location.reload();
    }
  } catch (error) {
    console.error('Error accepting registration:', error);

    if (regElement) {
      regElement.style.opacity = '1';
      regElement.style.pointerEvents = 'auto';
    }

    const message = error instanceof Error ? error.message : 'Failed to accept registration';
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'error');
    } else {
      alert('Error: ' + message);
    }
  }
}

/**
 * Handle rejecting a registration
 */
export async function handleRejectRegistration(tournamentId: string, registrationId: string): Promise<void> {
  const reason = prompt('Reject this registration? The player will be notified by email.\n\nOptional: Enter a reason for rejection:');

  // User cancelled
  if (reason === null) {
    return;
  }

  const regElement = document.getElementById(`pending-registration-${registrationId}`);
  if (regElement) {
    regElement.style.opacity = '0.5';
    regElement.style.pointerEvents = 'none';
  }

  try {
    await rejectRegistration(tournamentId, registrationId);

    // Save active tab before reload to stay on players tab
    sessionStorage.setItem(`active-tab-${tournamentId}`, 'players');

    // Reload tournament data
    if (typeof window.loadTournament === 'function') {
      await window.loadTournament(tournamentId);
    } else {
      // Fallback: reload page
      window.location.reload();
    }
  } catch (error) {
    console.error('Error rejecting registration:', error);

    if (regElement) {
      regElement.style.opacity = '1';
      regElement.style.pointerEvents = 'auto';
    }

    const message = error instanceof Error ? error.message : 'Failed to reject registration';
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'error');
    } else {
      alert('Error: ' + message);
    }
  }
}

// Export to window for onclick handlers
window.handleAcceptRegistration = handleAcceptRegistration;
window.handleRejectRegistration = handleRejectRegistration;
